import type { HsHypersceneTypes } from '../types/hyperscene-types.type';

export class HsColor {
  public readonly values: HsHypersceneTypes.Array3;

  constructor(r: number, g: number, b: number) {
    this.values = [r, g, b];
  }

  /**
   * Handles r.
   * @returns Result value.
   */
  public get r(): number {
    return this.values[0];
  }

  /**
   * Handles g.
   * @returns Result value.
   */
  public get g(): number {
    return this.values[1];
  }

  /**
   * Handles b.
   * @returns Result value.
   */
  public get b(): number {
    return this.values[2];
  }

  /**
   * Sets set.
   * @param r r.
   * @param g g.
   * @param b b.
   * @returns Result value.
   */
  public set(r: number, g: number, b: number): HsColor {
    this.values[0] = r;
    this.values[1] = g;
    this.values[2] = b;
    return this;
  }

  /**
   * Copies copy.
   * @param other other.
   * @returns Result value.
   */
  public copy(other: HsColor): HsColor {
    return this.set(other.r, other.g, other.b);
  }

  /**
   * Sets from array.
   * @param array array.
   * @returns Result value.
   */
  public setFromArray(array: HsHypersceneTypes.Array3): HsColor {
    this.values[0] = array[0];
    this.values[1] = array[1];
    this.values[2] = array[2];
    return this;
  }

  /**
   * Sets from lerp.
   * @param color1 color1.
   * @param color2 color2.
   * @param ratio ratio.
   * @returns Result value.
   */
  public setFromLerp(color1: HsColor, color2: HsColor, ratio: number): HsColor {
    const r: number = color1.r * (1 - ratio) + color2.r * ratio;
    const g: number = color1.g * (1 - ratio) + color2.g * ratio;
    const b: number = color1.b * (1 - ratio) + color2.b * ratio;
    return this.set(r, g, b);
  }

  /**
   * Handles multiply by value.
   * @param intensity intensity.
   * @returns Result value.
   */
  public multiplyByValue(intensity: number): HsColor {
    return this.set(this.r * intensity, this.g * intensity, this.b * intensity);
  }

  /**
   * Adds add.
   * @param other other.
   * @param intensity intensity.
   * @returns Result value.
   */
  public add(other: HsColor, intensity: number): HsColor {
    return this.set(this.r + other.r * intensity, this.g + other.g * intensity, this.b + other.b * intensity);
  }

  /**
   * Handles equals.
   * @param other other.
   * @returns Result value.
   */
  public equals(other: HsColor): boolean {
    return this.r === other.r && this.g === other.g && this.b === other.b;
  }

  /**
   * Builds black.
   * @returns Result value.
   */
  public static makeBlack(): HsColor {
    return new HsColor(0, 0, 0);
  }

  /**
   * Builds white.
   * @returns Result value.
   */
  public static makeWhite(): HsColor {
    return new HsColor(1, 1, 1);
  }

  /**
   * Builds red.
   * @returns Result value.
   */
  public static makeRed(): HsColor {
    return new HsColor(1, 0, 0);
  }

  /**
   * Builds green.
   * @returns Result value.
   */
  public static makeGreen(): HsColor {
    return new HsColor(0, 1, 0);
  }

  /**
   * Builds blue.
   * @returns Result value.
   */
  public static makeBlue(): HsColor {
    return new HsColor(0, 0, 1);
  }

  /**
   * Builds from rgb hex.
   * @param value value.
   * @returns Result value.
   */
  public static makeFromRGBHex(value: number): HsColor {
    const r: number = ((value >> 16) & 255) / 255;
    const g: number = ((value >> 8) & 255) / 255;
    const b: number = (value & 255) / 255;
    return new HsColor(r, g, b);
  }

  /**
   * Builds from rgb integers.
   * @param r r.
   * @param g g.
   * @param b b.
   * @returns Result value.
   */
  public static makeFromRGBIntegers(
    r: HsHypersceneTypes.Uint8,
    g: HsHypersceneTypes.Uint8,
    b: HsHypersceneTypes.Uint8
  ): HsColor {
    return new HsColor(r / 255, g / 255, b / 255);
  }

  /**
   * Builds from hsv.
   * @param h h.
   * @param s s.
   * @param v v.
   * @returns Result value.
   */
  public static makeFromHSV(h: number, s: number, v: number): HsColor {
    const phase: number = Math.floor(h * 6);
    const phaseShiftedHue: number = h * 6 - phase;
    const lightness1: number = v * (1 - s);
    const lightness2: number = v * (1 - phaseShiftedHue * s);
    const lightness3: number = v * (1 - (1 - phaseShiftedHue) * s);
    switch (phase % 6) {
      case 0:
        return new HsColor(v, lightness3, lightness1);
      case 1:
        return new HsColor(lightness2, v, lightness1);
      case 2:
        return new HsColor(lightness1, v, lightness3);
      case 3:
        return new HsColor(lightness1, lightness2, v);
      case 4:
        return new HsColor(lightness3, lightness1, v);
      default:
        return new HsColor(v, lightness1, lightness2);
    }
  }

  /**
   * Builds from style.
   * @param style style.
   * @returns Result value.
   */
  public static makeFromStyle(style: string): HsColor {
    const hexArray: RegExpExecArray | null = /^\#([A-Fa-f0-9]+)$/.exec(style);
    if (hexArray === null) {
      return new HsColor(0, 0, 0);
    }
    const hex: string = hexArray[1];
    const size: number = hex.length;
    if (size === 3) {
      const r: number = parseInt(hex.charAt(0) + hex.charAt(0), 16) / 255;
      const g: number = parseInt(hex.charAt(1) + hex.charAt(1), 16) / 255;
      const b: number = parseInt(hex.charAt(2) + hex.charAt(2), 16) / 255;
      return new HsColor(r, g, b);
    }
    if (size === 6) {
      const r: number = parseInt(hex.charAt(0) + hex.charAt(1), 16) / 255;
      const g: number = parseInt(hex.charAt(2) + hex.charAt(3), 16) / 255;
      const b: number = parseInt(hex.charAt(4) + hex.charAt(5), 16) / 255;
      return new HsColor(r, g, b);
    }
    return new HsColor(0, 0, 0);
  }

  /**
   * Builds copy.
   * @param source source.
   * @returns Result value.
   */
  public static makeCopy(source: HsColor): HsColor {
    return new HsColor(source.r, source.g, source.b);
  }
}
